// web/src/components/Attachments.tsx
import { useEffect, useRef, useState } from 'react';

interface Att { uuid: string; filename: string; mime: string | null; byte_size: number; caption: string | null; kind: string; created_at: string; }

const isImage = (a: Att) => a.kind === 'image' || (a.mime?.startsWith('image/') ?? false);
const revealUrl = (uuid: string) => `/api/attachments/${uuid}/reveal`;

// Downscale a captured/selected image in the browser (canvas → JPEG) so the stored
// copy is sensible for both the app (full-size view) and report thumbnails — phone
// photos are otherwise several MB. Uses a data URL (allowed by the CSP) not blob:.
async function resizeImage(file: File, maxDim = 1280, quality = 0.82): Promise<{ blob: Blob; filename: string }> {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') return { blob: file, filename: file.name };
  try {
    const dataUrl: string = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result as string); fr.onerror = rej; fr.readAsDataURL(file); });
    const img: HTMLImageElement = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl; });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    if (scale >= 1 && file.size < 500_000) return { blob: file, filename: file.name };
    const w = Math.max(1, Math.round(img.width * scale)), h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
    canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
    const blob: Blob | null = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
    if (!blob) return { blob: file, filename: file.name };
    const base = (file.name.replace(/\.[^.]+$/, '') || 'photo');
    return { blob, filename: `${base}.jpg` };
  } catch { return { blob: file, filename: file.name }; }
}

export function Attachments({ entityType, entityUuid, compact = false }: { entityType: string; entityUuid?: string; compact?: boolean }) {
  const [items, setItems] = useState<Att[]>([]);
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const camRef = useRef<HTMLInputElement | null>(null);

  const qs = new URLSearchParams({ entity_type: entityType, ...(entityUuid ? { entity_uuid: entityUuid } : {}) }).toString();
  const load = () => fetch(`/api/attachments?${qs}`).then((r) => r.json()).then((d) => setItems(d.attachments ?? []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, entityUuid]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Close the lightbox on Escape.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightbox]);

  async function uploadBlob(blob: Blob, filename: string, cap?: string) {
    setBusy(true); setErr(null);
    const fd = new FormData();
    fd.append('entity_type', entityType);
    if (entityUuid) fd.append('entity_uuid', entityUuid);
    fd.append('kind', blob.type.startsWith('image/') ? 'image' : 'file');
    if (cap) fd.append('caption', cap);
    fd.append('file', blob, filename);
    try {
      const res = await fetch('/api/attachments', { method: 'POST', body: fd });
      if (!res.ok) { setErr('Upload failed — please try again.'); return false; }
      setFile(null); setCaption(''); await load(); return true;
    } catch { setErr('Upload failed — could not reach the server.'); return false; }
    finally { setBusy(false); }
  }

  async function uploadPending() {
    if (!file) return;
    const { blob, filename } = await resizeImage(file);
    await uploadBlob(blob, filename, caption);
  }

  // Camera capture → resize → save immediately (one tap to photograph a device).
  async function onCamera(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    const { blob, filename } = await resizeImage(f);
    await uploadBlob(blob, filename, caption);
  }

  async function erase(uuid: string) {
    if (!confirm('Erase this attachment? This crypto-shreds the file (kept only as a ledger commitment).')) return;
    await fetch(`/api/attachments/${uuid}`, { method: 'DELETE' }); load();
  }

  const images = items.filter(isImage);
  const files = items.filter((a) => !isImage(a));

  const thumb = (a: Att, size: number) => (
    <button key={a.uuid} type="button" title={a.caption ?? a.filename} onClick={() => setLightbox(a.uuid)}
      style={{ padding: 0, border: '1px solid var(--border2)', borderRadius: 6, overflow: 'hidden', width: size, height: size, cursor: 'zoom-in', background: 'var(--surface2)', flex: '0 0 auto' }}>
      <img src={revealUrl(a.uuid)} alt={a.caption ?? a.filename} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </button>
  );

  const body = (
    <>
      <h4>Photos &amp; files</h4>
      {images.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {images.map((a) => (
            <div key={a.uuid} style={{ position: 'relative' }}>
              {thumb(a, 64)}
              <button type="button" onClick={() => erase(a.uuid)} title="Erase"
                style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, lineHeight: '16px', borderRadius: '50%', border: '1px solid var(--border2)', background: 'var(--surface)', cursor: 'pointer', fontSize: 11, padding: 0 }}>×</button>
            </div>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <ul style={{ margin: '0 0 8px', paddingLeft: 16, fontSize: 12.5 }}>
          {files.map((a) => (
            <li key={a.uuid}>
              <a href={revealUrl(a.uuid)} target="_blank" rel="noreferrer">{a.filename}</a>
              {a.caption ? ` — ${a.caption}` : ''} <small className="muted3">({Math.round(a.byte_size / 1024)} KB)</small>{' '}
              <button className="btn btn-ghost" style={{ fontSize: 11, padding: '1px 6px' }} onClick={() => erase(a.uuid)}>Erase</button>
            </li>
          ))}
        </ul>
      )}
      {items.length === 0 && <div className="muted3" style={{ fontSize: 12.5, marginBottom: 8 }}>No photos or files yet.</div>}

      <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onCamera} style={{ display: 'none' }} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="btn btn-primary" style={{ fontSize: 12, padding: '4px 10px' }} disabled={busy} onClick={() => camRef.current?.click()}>📷 Take photo</button>
        <label className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px', cursor: 'pointer', margin: 0 }}>
          Choose file
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} style={{ display: 'none' }} />
        </label>
        {file && (
          <>
            <span className="muted3" style={{ fontSize: 12, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
            <button type="button" className="btn btn-primary" style={{ fontSize: 12, padding: '4px 10px' }} disabled={busy} onClick={uploadPending}>{busy ? 'Uploading…' : 'Upload'}</button>
          </>
        )}
      </div>
      <input placeholder="Caption (optional) — applies to the next photo/file" value={caption} onChange={(e) => setCaption(e.target.value)} style={{ marginTop: 6, width: '100%', fontSize: 12, boxSizing: 'border-box' }} />
      {busy && !file && <div className="muted3" style={{ fontSize: 12, marginTop: 4 }}>Saving photo…</div>}
      {err && <div style={{ color: 'var(--bad)', fontSize: 12, marginTop: 4 }}>{err}</div>}
    </>
  );

  const lightboxEl = lightbox && (
    <div onClick={() => setLightbox(null)}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(10,14,22,0.86)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, cursor: 'zoom-out' }}>
      <img src={revealUrl(lightbox)} alt="attachment" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6, boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }} />
      <button type="button" aria-label="Close" onClick={() => setLightbox(null)}
        style={{ position: 'fixed', top: 14, right: 16, fontSize: 22, lineHeight: 1, color: '#fff', background: 'transparent', border: 'none', cursor: 'pointer' }}>×</button>
    </div>
  );

  // Compact mode: small inline thumbnails on the row + a popover to add/manage.
  if (compact) {
    return (
      <div ref={popRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6 }}>
        {images.slice(0, 4).map((a) => thumb(a, 30))}
        <button className="btn btn-ghost" style={{ fontSize: 12 }} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          📷{items.length ? ` (${items.length})` : ''}
        </button>
        {open && (
          <div className="attachments" style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 30, width: 300, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', boxShadow: '0 8px 28px rgba(16,24,40,0.16)', textAlign: 'left' }}>
            {body}
          </div>
        )}
        {lightboxEl}
      </div>
    );
  }

  return <div className="attachments">{body}{lightboxEl}</div>;
}
